import React from 'react';
import PropTypes from 'prop-types';
import Col from 'components/Col';
import Row from 'components/Row';
import VolumeCalculator from 'helpers/volumeCalculator';
import ValidationGroup from 'helpers/validation';
import LayoutInput from 'components/forms/layoutInput';
import LayoutSelect from 'components/forms/layoutSelect';
import {systemTypes, chemicalBaseTypes, systemTypeNamesFormat} from 'helpers/constants';
import {systemOptionsTags} from 'helpers/dataHelpers';
import getProductDict from 'page/customer/system/productDict';
import {balanceColumns} from 'helpers/fieldHelpers';
import LayoutCheckbox from 'components/forms/layoutCheckbox';
import Loader from 'components/loader';
import productAPI from 'api/product';
import dealerAPI from 'api/dealer';
import session from 'services/sessionService';
import cloneDeep from 'lodash/cloneDeep';
import Button from 'components/layout/Button';
import Pool from 'models/pool';
import moment from 'moment';
import {FormattedMessage, injectIntl, intlShape} from 'react-intl';
import {inSoftSoakTrioRange} from 'helpers/regionalDifferences';
import { isBalanceCompleteSanitizer } from '../../../../helpers/constants';

class PoolProperties extends React.Component {
  static initialState = {
    loaded: false,
    products: [],
    dealer: undefined,
    pool: new Pool({
      volume: undefined,
      typeID: systemTypes.pool,
      finish: 'U',
      property: 'R',
      location: 'O',
      filterMedia: '',
      sanitizer: undefined,
      maintOxidizer: undefined,
      prevAlgicide: undefined,
      maintClarifier: undefined,
      usingNaturalResult: false,
      scalePrevention: undefined,
    }),
    seasonUsage: {
      weeks: undefined,
    },
    productDict: {},
  };

  static propTypes = {
    onSubmit: PropTypes.func.isRequired,
    intl: intlShape,
  };

  constructor(props) {
    super(props);

    this.group = new ValidationGroup(this.submit);
  }

  state = PoolProperties.initialState;

  componentWillMount() {
    this.fetchData();
  }

  reset = () => {
    const {products, dealer} = this.state;
    const {productDict, pool} = getProductDict({
      pool: new Pool({
        volume: undefined,
        typeID: systemTypes.pool,
        finish: 'U',
        property: 'R',
        location: 'O',
        filterMedia: '',
        sanitizer: undefined,
        maintOxidizer: undefined,
        prevAlgicide: undefined,
        maintClarifier: undefined,
        usingNaturalResult: false,
        scalePrevention: undefined,
      }),
      products,
      dealer,
      formatMessage: this.props.intl.formatMessage,
    });

    this.setState({
      pool,
      productDict,
      seasonUsage: {
        weeks: undefined,
      },
    });
  };

  fetchData() {
    return Promise.all([
      productAPI.getProducts(),
      dealerAPI.getDealer(session.getUser().dealerId),
    ])
      .then(([products, dealer]) => {
        const {pool, productDict} = getProductDict({pool: this.state.pool, products, dealer, formatMessage: this.props.intl.formatMessage});

        this.setState({
          products,
          dealer,
          pool,
          loaded: true,
          productDict,
        });
      });
  }

  _handler = key1 => ({key, value}) => {
    let obj = cloneDeep(this.state[key1]);

    obj[key] = value;

    let pDict = this.state.productDict;

    if (key1 === 'pool') {
      let {productDict, pool} = getProductDict({
        pool: obj,
        products: this.state.products,
        dealer: this.state.dealer,
        prevPool: this.state.pool,
        formatMessage: this.props.intl.formatMessage,
      });

      pDict = productDict;
      obj = pool;
    }

    this.setState({[key1]: obj, productDict: pDict});
  };

  updatePool = this._handler('pool');
  updateSeasonUsage = this._handler('seasonUsage');

  getInputs = () => {
    const {formatMessage} = this.props.intl;
    let all = [];
    let {pool, seasonUsage: su, productDict, dealer} = this.state;
    let options = systemOptionsTags[pool.typeID];

    const simpleFormat = id => formatMessage({id});

    const systemTypeNames = systemTypeNamesFormat(formatMessage);

    all.push(<div key='volume' className='form-group'>
      <label><FormattedMessage id='general.volume'/><span className='error' style={{marginLeft: 2}}>*</span></label>
      <VolumeCalculator
        dataKey='volume'
        volume={pool.volume}
        group={this.group}
        poolType={pool.typeID}
        region={dealer.country}
        beforeFocus={false}
        title={formatMessage({id: 'system.details.select.calculate'})}
        editMode={true}
        onBlur={this.updatePool}/>
    </div>);

    all.push(<LayoutInput key='weeks'
                          dataKey='weeks'
                          label={formatMessage({id: 'quick-calcs.seasons-usage.weeks'})}
                          type='number'
                          required
                          group={this.group}
                          value={su.weeks}
                          dataChanged={this.updateSeasonUsage}/>);


    all.push(<LayoutSelect key='typeID'
                           dataKey='typeID'
                           label={formatMessage({id: 'general.system.type'})}
                           value={pool.typeID}
                           dataChanged={this.updatePool}
                           keys={Object.keys(systemTypeNames)}
                           options={Object.values(systemTypeNames)}/>);

    all.push(<LayoutSelect key='finish'
                           dataKey='finish'
                           label={formatMessage({id: 'general.finish'})}
                           value={pool.finish}
                           dataChanged={this.updatePool}
                           options={Object.values(options.finish).map(simpleFormat)}
                           keys={Object.keys(options.finish)}/>);

    all.push(<LayoutSelect key='property'
                           dataKey='property'
                           label={formatMessage({id: 'general.installation.type'})}
                           value={pool.property}
                           dataChanged={this.updatePool}
                           options={Object.values(options.property).map(simpleFormat)}
                           keys={Object.keys(options.property)}/>);

    all.push(<LayoutSelect key='location'
                           dataKey='location'
                           label={formatMessage({id: 'general.location'})}
                           value={pool.location}
                           dataChanged={this.updatePool}
                           options={Object.values(options.location).map(simpleFormat)}
                           keys={Object.keys(options.location)}/>);

    all.push(<LayoutSelect label={formatMessage({id: 'general.filter.media'})}
                           dataKey='filterMedia'
                           key='filterMedia'
                           value={pool.filterMedia}
                           dataChanged={this.updatePool}
                           options={Object.values(options.filterMedia).map(simpleFormat)}
                           keys={Object.keys(options.filterMedia)}
                           condition={pool.typeID !== systemTypes.spa}/>);

    all.push(<LayoutSelect label={formatMessage({id: 'general.sanitizer'})}
                           key='sanitizer'
                           dataKey='sanitizer'
                           dataChanged={this.updatePool}
                           options={productDict.sanitizer.names}
                           keys={productDict.sanitizer.keys}
                           value={pool.sanitizer}/>);

    all.push(<LayoutSelect label={formatMessage({id: 'general.maint.oxidizer'})}
                           key='maintOxidizer'
                           dataKey='maintOxidizer'
                           dataChanged={this.updatePool}
                           options={productDict.maintOxidizer.names}
                           keys={productDict.maintOxidizer.keys}
                           value={pool.maintOxidizer}/>);

    all.push(<LayoutSelect label={formatMessage({id: 'general.prev.algicide'})}
                           key='prevAlgicide'
                           dataKey='prevAlgicide'
                           dataChanged={this.updatePool}
                           options={productDict.prevAlgicide.names}
                           keys={productDict.prevAlgicide.keys}
                           value={pool.prevAlgicide}/>);

    all.push(<LayoutSelect label={formatMessage({id: 'general.maint.clarifier'})}
                           key='maintClarifier'
                           dataKey='maintClarifier'
                           dataChanged={this.updatePool}
                           options={productDict.maintClarifier.names}
                           keys={productDict.maintClarifier.keys}
                           value={pool.maintClarifier}/>);

    all.push(<LayoutSelect editMode={true}
                           label={formatMessage({id: 'general.stabilizer'})}
                           dataKey='stabilizer'
                           key='stabilizer'
                           value={productDict.stabilizer.current}
                           dataChanged={this.updatePool}
                           options={productDict.stabilizer.names}
                           keys={productDict.stabilizer.keys}
                           ref={r => this.stabilizer = r}
                           condition={pool.typeID === systemTypes.pool &&
                           [
                             chemicalBaseTypes.chlorinePool,
                             chemicalBaseTypes.mineralSpringsPool,
                             chemicalBaseTypes.saltWaterPool,
                           ].includes(productDict.sanitizer.chemicalBaseType)}
    />);
    all.push(<LayoutSelect editMode={true}
                           label={formatMessage({id: 'general.ph.buffer'})}
                           dataKey='pHBuffer'
                           key='pHBufer'
                           value={productDict.pHBuffer.current}
                           dataChanged={this.updatePool}
                           options={productDict.pHBuffer.names}
                           keys={productDict.pHBuffer.keys}
                           ref={r => this.pHBuffer = r}
                           condition={pool.typeID === systemTypes.spa && !pool.softSoakTRIO &&
                           productDict.sanitizer.currentName !== 'Saltwater Spa' && !pool.usingVanishingAct}
    />);

    all.push(<LayoutSelect editMode={true}
      label={formatMessage({id: 'customer.product.profile.scale-prevention'})}
      dataKey='scalePrevention'
      key='scalePrevention'
      value={productDict.scalePrevention.current}
      dataChanged={this.updatePool}
      options={productDict.scalePrevention.names.filter(name => name != 'SaltScapes Scale Defender')}
      keys={productDict.scalePrevention.keys}
    />);

    all.push(<LayoutCheckbox label={formatMessage({id: 'general.natural.result'})}
                             key='usingNaturalResult'
                             dataKey='usingNaturalResult'
                             dataChanged={this.updatePool}
                             condition={pool.typeID === systemTypes.pool}
                             value={pool.usingNaturalResult}/>);
    
    all.push(<LayoutCheckbox label={formatMessage({id: 'general.balanceComplete'})}
                             key='balanceComplete'
                             dataKey='balanceComplete'
                             dataChanged={this.updatePool}
                             condition={isBalanceCompleteSanitizer(productDict.sanitizer.currentName) && dealer.country === 'US'}
                             value={pool.balanceComplete}/>);


    all.push(<LayoutCheckbox editMode={true}
                             label={formatMessage({id: 'chemicals.natural.spa.enzyme'})}
                             dataKey='enzymes'
                             key='enzymes'
                             value={pool.enzymes}
                             dataChanged={this.updatePool}
                             condition={(pool.typeID === systemTypes.spa || pool.typeID === systemTypes.swimspa)} />);

    // [ALEX-2785] Remove Pool Opening Complete field
    /* all.push(<LayoutCheckbox editMode={true}
                             label={formatMessage({id: 'chemicals.pool.opening.complete'})}
                             dataKey='poolOpeningComplete' key='poolOpeningComplete'
                             condition={pool.typeID === systemTypes.pool}
                             value={pool.poolOpeningComplete}
                             dataChanged={this.updatePool} />); */

    all.push(<LayoutCheckbox editMode={true}
                             label={formatMessage({id: 'chemicals.oxysheen'})}
                             dataKey='oxysheenTreatment'
                             key='oxysheenTreatment'
                             condition={productDict.sanitizer.chemicalBaseType === chemicalBaseTypes.softswimPool}
                             value={pool.oxysheenTreatment}
                             dataChanged={this.updatePool} />);

    all.push(<LayoutCheckbox editMode={true}
                             label={formatMessage({id: 'chemicals.spa.complete'})}
                             dataKey='spaComplete'
                             key='spaComplete'
                             condition={pool.typeID === systemTypes.spa}
                             value={pool.spaComplete}
                             dataChanged={this.updatePool} />);

    all.push(<LayoutCheckbox editMode={true}
                             label={formatMessage({id: 'chemicals.softsoak.trio'})}
                             dataKey='softSoakTRIO'
                             key='softSoakTRIO'
                             condition={parseInt(pool.typeID) === systemTypes.spa &&
                               productDict.sanitizer.chemicalBaseType !== chemicalBaseTypes.softSoakSpa &&
                               inSoftSoakTrioRange(dealer, pool) &&
                               productDict.sanitizer.chemicalBaseType !== chemicalBaseTypes.synergySpa}
                             value={pool.softSoakTRIO}
                             dataChanged={this.updatePool} />);

    return balanceColumns(all, 3);
  };

  submit = () => {
    const {pool, seasonUsage} = this.state;

    pool.seasonStart = moment().startOf('year');
    pool.seasonEnd = moment().startOf('year').add(seasonUsage.weeks, 'weeks');

    this.props.onSubmit(pool);
  };

  render() {
    return <div className='qc-su-properties'>
      <p className='qc-su-properties__instructions'><FormattedMessage id='quick-calcs.seasons-usage.instructions'/></p>
      <div className='qc-su-properties__input'>
        <Loader loaded={this.state.loaded}>
          {this.state.loaded && this.getInputs()}

          <Row>
            <Col md={12}>
              <div className='qc-su-properties__buttons pull-right'>
                <Button link onClick={this.reset}><FormattedMessage id='quick-calcs.reset'/></Button>
                <Button primary onClick={this.group.validate}><FormattedMessage id='quick-calcs.calculate'/></Button>
              </div>
            </Col>
          </Row>
        </Loader>
      </div>
    </div>;
  }
}

export default injectIntl(PoolProperties);
